'use client';

import React from 'react';
import useDevStore from '../../stores/useDevStore';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from '../../stores/orderbook/useOrderbookDisplayStore';
import { V2OrderbookDisplayProps } from '../Orderbook/OrderbookTypes';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { useTargetMarketSlotStaleness } from 'src/hooks/useTargetMarketSlotStaleness';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import Select from 'src/components/primitives/Select/Select';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';

const DATASOURCE_OPTIONS = [
	{
		value: OrderbookSource.BLOCKCHAIN,
		label: 'Blockchain',
	},
	{
		value: OrderbookSource.POLLING,
		label: 'Polling',
	},
	{
		value: OrderbookSource.WEBSOCKET,
		label: 'Websocket',
	},
];

const getSlotStalenessColor = (staleness: number) => {
	if (staleness <= 0) return 'text-positive-green';
	if (staleness > 1) return 'text-star-yellow';
	if (staleness > 4) return 'text-negative-red';
	return 'text-text-default';
};

const DevOrderbookPanel = (props: V2OrderbookDisplayProps) => {
	const { set, dlobSettings } = useDevStore();

	const toggleDlobSetting = (val: keyof typeof dlobSettings) => {
		set((s) => {
			s.dlobSettings[val] = !s.dlobSettings[val];
		});
	};

	const orderbookStore = useOrderbookDisplayStore();

	const setDataSource = (newSelection: OrderbookSource) => {
		orderbookStore.set((s) => {
			s.orderbookSource = newSelection;
			// Disable auto-switching for manual non-websocket selections
			// Enable auto-switching when manually selecting websocket (to restore automatic behavior)
			s.disableAutoSourceSwitching = newSelection !== OrderbookSource.WEBSOCKET;
		});
	};

	const dataSource = useOrderbookDisplayStore((s) => s.orderbookSource);

	const {
		l2SlotStaleness: l2SlotStaleness,
		oracleSlotStaleness,
		marketSlotStaleness,
		mmOracleSlotStaleness,
	} = useTargetMarketSlotStaleness(props.marketId);

	const {
		selectOptions,
		selectedOption,
		value,
		onValueChange,
		open,
		onOpenChange,
	} = useSelect({
		options: DATASOURCE_OPTIONS,
		selectedValue: dataSource,
		onSelectedValueChange: setDataSource,
	});

	return (
		<div className="flex flex-col gap-y-sm px-xs pt-md typo-b4">
			<div className="flex flex-col gap-y-xs">
				<div className={getSlotStalenessColor(l2SlotStaleness)}>
					L2: {l2SlotStaleness == undefined ? 'Invalid' : l2SlotStaleness}
				</div>
				<div className={getSlotStalenessColor(marketSlotStaleness)}>
					Market:{' '}
					{marketSlotStaleness == undefined ? 'Invalid' : marketSlotStaleness}
				</div>
				<div className={getSlotStalenessColor(oracleSlotStaleness)}>
					Oracle:{' '}
					{oracleSlotStaleness == undefined ? 'Invalid' : oracleSlotStaleness}
				</div>
				<div className={getSlotStalenessColor(mmOracleSlotStaleness)}>
					MMOracle:{' '}
					{mmOracleSlotStaleness == undefined
						? 'Invalid'
						: mmOracleSlotStaleness}
				</div>
			</div>

			<div className="flex flex-col gap-y-xs">
				<Tooltip
					className="w-fit typo-b4 whitespace-nowrap text-text-secondary"
					content="This will be the source of the orderbook and oracle price used in liquidity+trading calculations"
				>
					Orderbook Data Source
				</Tooltip>
				<Select.Root
					open={open}
					onOpenChange={onOpenChange}
					value={value}
					onValueChange={onValueChange}
				>
					<Select.Trigger size="sm">{selectedOption?.label}</Select.Trigger>
					<Select.Options>
						{selectOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>
			</div>

			<div className="flex flex-col gap-y-xs">
				<span className="typo-b4 text-text-secondary">
					Liquidity Display Sources
				</span>
				<div className="flex flex-wrap gap-x-md gap-y-xs">
					<Checkbox.Default
						id="show-dlob"
						label="DLOB"
						checked={dlobSettings.showDlob}
						onCheckedChange={() => toggleDlobSetting('showDlob')}
					/>
					{props.marketId?.isPerp ? (
						<>
							<Checkbox.Default
								id="show-vamm"
								label="VAMM"
								checked={dlobSettings.showVamm}
								onCheckedChange={() => toggleDlobSetting('showVamm')}
							/>
							<Checkbox.Default
								id="show-indicative"
								label="Indicative"
								checked={dlobSettings.showIndicative}
								onCheckedChange={() => toggleDlobSetting('showIndicative')}
							/>
						</>
					) : (
						<>
							<Checkbox.Default
								id="show-serum"
								label="openbook"
								checked={dlobSettings.showSerum}
								onCheckedChange={() => toggleDlobSetting('showSerum')}
							/>
							<Checkbox.Default
								id="show-phoenix"
								label="phoenix"
								checked={dlobSettings.showPhoenix}
								onCheckedChange={() => toggleDlobSetting('showPhoenix')}
							/>
						</>
					)}
					<Checkbox.Default
						id="uncross"
						label="Uncross"
						checked={!dlobSettings.disableUncross}
						onCheckedChange={() => toggleDlobSetting('disableUncross')}
					/>
				</div>
			</div>
		</div>
	);
};

export default DevOrderbookPanel;
